import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { MsalService } from '@azure/msal-angular';

const GRAPH_SCOPES = ['https://graph.microsoft.com/.default'];

@Injectable({
    providedIn: 'root'
})
export class GraphService {
    constructor(
        private http: HttpClient,
        private authService: MsalService
    ) { }

    private async getGraphToken(): Promise<string> {
        const account = this.authService.instance.getActiveAccount();

        if (!account) {
            throw new Error('[GraphService] No active MSAL account. User must be logged in.');
        }

        const tokenResponse = await lastValueFrom(
            this.authService.acquireTokenSilent({
                scopes: GRAPH_SCOPES,
                account
            })
        );

        return tokenResponse.accessToken;
    }

    public async getUserPhoto(): Promise<any> {
        const graphToken = await this.getGraphToken();
        const url = "https://graph.microsoft.com/v1.0/me/photo/$value";
        const headers: HttpHeaders = new HttpHeaders({
            Authorization: `Bearer ${graphToken}`
        });
        const response: any = await lastValueFrom(this.http.get(url, { headers: headers, responseType: "blob" }));

        return await new Promise<any>((resolve, reject) => {
            if (!response) {
                resolve("x");
                return;
            }

            const reader: FileReader = new FileReader();
            reader.readAsDataURL(response);
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => reject(reader.error);
        });
    }

    public async getUserGroups(role: any): Promise<any> {
        let results: any = null;

        try {
            const token = await this.getGraphToken();
            
            let url = `https://graph.microsoft.com/v1.0/me/transitiveMemberOf/microsoft.graph.group?$count=true&$orderby=displayName&$filter=startswith(displayName, '${role}')`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${token}`,
                ConsistencyLevel: "eventual"
            });
            const allGroups: any[] = [];

            while (url) {
                const stream$ = this.http.get(url, { headers: headers });
                const page: any = await lastValueFrom(stream$);
                const groups = Array.isArray(page?.value) ? page.value : [];
                allGroups.push(...groups);
                url = page?.['@odata.nextLink'] || '';
            }

            results = { value: allGroups };
        } catch (error: any) {
            throw error;
        }

        return results;
    }

    public async checkUserInGroup(groupId: string): Promise<boolean> {
        try {
            const token = await this.getGraphToken();
            
            let url = `https://graph.microsoft.com/v1.0/me/memberOf`;
            let headers: HttpHeaders = new HttpHeaders({
                Authorization: `Bearer ${token}`
            });

            while (url) {
                const stream$ = this.http.get(url, { headers: headers });
                const result: any = await lastValueFrom(stream$);

                if (result?.value && Array.isArray(result.value)) {
                    const isMember = result.value.some((group: any) => group.id === groupId);
                    if (isMember) {
                        return true;
                    }
                }

                url = result?.['@odata.nextLink'] || '';
            }
            
            return false;
        } catch (error: any) {
            console.error('Error checking group membership:', error);
            return false;
        }
    }
}

